// /Users/apichet/Downloads/cheetah-insurance-app/src/context/SearchContext.js
// /Users/apichet/Downloads/cheetah-insurance-app/src/context/SearchContext.js
import React, { createContext, useReducer, useContext, useMemo, useEffect } from "react";

const DEV = process.env.NODE_ENV !== "production";
const log = (...a) => DEV && console.log(...a);
const warn = (...a) => DEV && console.warn(...a);

// ----- Storage -----
const STORAGE_KEY = "searchState:v1";

// ----- State / Actions -----
const initialState = {
  results: [],
  page: 1,
  totalPages: 1,
  filters: {},
};

const ACTIONS = Object.freeze({
  SAVE_SEARCH_RESULTS: "SAVE_SEARCH_RESULTS",
  RESET_SEARCH: "RESET_SEARCH",
  LOAD_PREVIOUS_SEARCH: "LOAD_PREVIOUS_SEARCH",
  UPDATE_FILTERS: "UPDATE_FILTERS",
});

// ----- Utilities -----
const safeParse = (txt, fallback = null) => {
  try {
    return JSON.parse(txt);
  } catch {
    return fallback;
  }
};

const isValidStateShape = (s) =>
  s &&
  typeof s === "object" &&
  Array.isArray(s.results) &&
  Number.isFinite(Number(s.page)) &&
  Number.isFinite(Number(s.totalPages)) &&
  typeof s.filters === "object";

const loadStoredState = () => {
  const raw = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
  const parsed = safeParse(raw, null);
  if (!isValidStateShape(parsed)) return null;
  return {
    results: Array.isArray(parsed.results) ? parsed.results : [],
    page: Math.max(1, Number(parsed.page) || 1),
    totalPages: Math.max(1, Number(parsed.totalPages) || 1),
    filters: typeof parsed.filters === "object" ? parsed.filters : {},
  };
};

// ----- Reducer -----
const reducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SAVE_SEARCH_RESULTS: {
      const p = action.payload || {};
      if (!Array.isArray(p.results)) {
        warn("❌ Invalid payload for SAVE_SEARCH_RESULTS:", action.payload);
        return state;
      }
      return {
        ...state,
        results: p.results,
        page: Math.max(1, Number(p.page) || 1),
        totalPages: Math.max(1, Number(p.total_pages ?? p.totalPages) || 1),
        // อนุญาตอัพเดต filters พร้อมกันถ้ามีส่งมา
        filters: typeof p.filters === "object" ? { ...state.filters, ...p.filters } : state.filters,
      };
    }

    case ACTIONS.RESET_SEARCH:
      return { ...initialState };

    case ACTIONS.LOAD_PREVIOUS_SEARCH: {
      const stored = loadStoredState();
      return stored ? { ...state, ...stored } : state;
    }

    case ACTIONS.UPDATE_FILTERS: {
      const patch = action.payload || {};
      if (typeof patch !== "object") {
        warn("⚠️ UPDATE_FILTERS expects object payload");
        return state;
      }
      return { ...state, filters: { ...state.filters, ...patch } };
    }

    default:
      warn("⚠️ Unknown action type:", action.type);
      return state;
  }
};

// ----- Context -----
const SearchContext = createContext(undefined);

// ----- Provider -----
export const SearchProvider = ({ children }) => {
  // init จาก localStorage ครั้งแรก
  const [state, dispatch] = useReducer(
    reducer,
    undefined,
    () => loadStoredState() || { ...initialState }
  );

  // persist ลง localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {}
  }, [state]);

  // dev log
  useEffect(() => {
    log("🔍 Current Search State:", state);
  }, [state]);

  // Action helpers (คง dispatch เดิมเพื่อไม่พังย้อนหลัง)
  const actions = useMemo(
    () => ({
      saveSearchResults: (payload) =>
        dispatch({ type: ACTIONS.SAVE_SEARCH_RESULTS, payload }),
      resetSearch: () => dispatch({ type: ACTIONS.RESET_SEARCH }),
      loadPreviousSearch: () => dispatch({ type: ACTIONS.LOAD_PREVIOUS_SEARCH }),
      updateFilters: (patch) => dispatch({ type: ACTIONS.UPDATE_FILTERS, payload: patch }),
    }),
    []
  );

  const value = useMemo(() => ({ state, dispatch, actions, ACTIONS }), [state, dispatch, actions]);

  return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>;
};

// ----- Hook -----
export const useSearch = () => {
  const ctx = useContext(SearchContext);
  if (!ctx) throw new Error("useSearch must be used within a SearchProvider");
  return ctx;
};

export default SearchContext;
